import { CheckOutlined } from '@ant-design/icons';
import { Button, Divider, Select, SelectProps } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { FC, ReactElement, useCallback, useMemo } from 'react';

import countries from '../../countries.json';
import CountryFlag from '../CountryFlag';

import styles from './styles.module.less';

const filterOption = (inputValue: string, option?: DefaultOptionType) => {
  if (!inputValue.trim() || !option) {
    return true;
  }

  const inputValueLowerCase = inputValue.toLowerCase();

  return (
    String(option.label).toLowerCase().includes(inputValueLowerCase) ||
    String(option.value).toLowerCase() === inputValueLowerCase
  );
};

type CountrySelectProps<T> = SelectProps<T> & {
  countries?: string[];
};

const CountrySelect: FC<CountrySelectProps<string | string[]>> = ({
  countries: countriesProp,
  maxTagCount = 10,
  mode,
  onChange,
  value,
  ...rest
}) => {
  const isSelectedAll =
    Array.isArray(value) && value.length === countries.length;

  const onSelectAll = useCallback(() => {
    if (!onChange) {
      return;
    }

    onChange(
      countries.map((country) => country.code),
      [],
    );
  }, [onChange]);

  const dropdownRender = useCallback(
    (menu: ReactElement) => {
      if (mode !== 'multiple') {
        return menu;
      }

      return (
        <>
          <Button
            className={styles.dropdownMenuItemButton}
            onClick={onSelectAll}
            onMouseDown={(e) => e.preventDefault()} // https://github.com/ant-design/ant-design/issues/13448#issuecomment-473198945
            type="link"
          >
            Select all
            {isSelectedAll && (
              <CheckOutlined className={styles.dropdownMenuItemSelectedIcon} />
            )}
          </Button>
          <Divider className={styles.dropdownMenuItemDivider} />
          {menu}
        </>
      );
    },
    [isSelectedAll, mode, onSelectAll],
  );

  const options = useMemo(() => {
    if (countriesProp) {
      return countries.filter(({ code }) => countriesProp.includes(code));
    }

    return countries;
  }, [countriesProp]);

  const placeholder =
    mode && ['tags', 'multiple'].includes(mode)
      ? 'Select Countries...'
      : 'Select Country...';

  // TODO: Integrate <EnhanceSelect /> to realize Select All functionality
  return (
    <Select
      allowClear
      dropdownRender={dropdownRender}
      filterOption={filterOption}
      maxTagCount={maxTagCount}
      maxTagPlaceholder={(values) => `+${values.length} more`}
      mode={mode}
      onChange={onChange}
      placeholder={placeholder}
      showSearch
      value={value}
      {...rest}
    >
      {options.map(({ code, name }) => (
        <Select.Option key={code} label={name} value={code}>
          <CountryFlag className={styles.icon} code={code} />
          {name}
        </Select.Option>
      ))}
    </Select>
  );
};

CountrySelect.displayName = 'CountrySelect';

export default CountrySelect;
